import React, { useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';
import './App.css';
import { getConfig } from './aws-config';

// Initialize Amplify with configuration
const awsConfig = getConfig();
Amplify.configure(awsConfig);

const AVAILABLE_MODELS = [
  {
    id: 'us.anthropic.claude-sonnet-4-6',
    name: 'Claude Sonnet 4.6',
    description: 'Latest balanced performance'
  },
  {
    id: 'us.anthropic.claude-opus-4-6-v1',
    name: 'Claude Opus 4.6',
    description: 'Highest capability, premium cost'
  },
  {
    id: 'us.amazon.nova-pro-v1:0',
    name: 'Amazon Nova Pro',
    description: 'Fast and cost-effective'
  },
  {
    id: 'us.meta.llama3-3-70b-instruct-v1:0',
    name: 'Meta Llama 3.3 70B',
    description: 'Open-source, consistent pricing'
  }
];

function Chat({ signOut, user, awsConfig }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRickroll, setShowRickroll] = useState(false);
  const [selectedModel, setSelectedModel] = useState(AVAILABLE_MODELS[0].id);

  const handleRickroll = () => {
    setShowRickroll(true);
  };

  const closeRickroll = () => {
    setShowRickroll(false);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    setMessages([...messages, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString();

      const response = await fetch(`${awsConfig.API.REST.chatApi.endpoint}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': idToken
        },
        body: JSON.stringify({
          message: input,
          model: selectedModel
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const assistantMessage = {
        role: 'assistant',
        content: data.message,
        model: data.model,
        metrics: data.metrics
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        role: 'error',
        content: `Error: ${error.message}. Please try again.`
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app">
      <header className="header">
        <h1>GenAI Chat</h1>
        <div className="user-info">
          <span>{user.signInDetails?.loginId}</span>
          <button onClick={signOut} className="sign-out-button">Sign Out</button>
        </div>
      </header>

      <div className="chat-container">
        <div className="messages">
          {messages.length === 0 && (
            <div className="welcome-message">
              <h2>Welcome to GenAI Chat!</h2>
              <p>Start a conversation with Claude powered by Amazon Bedrock.</p>
            </div>
          )}
          {messages.map((msg, index) => (
            <div key={index} className={`message ${msg.role}`}>
              <div className="message-content">
                {msg.content}
              </div>
              {msg.metrics && (
                <div className="message-metrics">
                  <div className="metrics-row">
                    <span className="metric-label">Model:</span>
                    <span className="metric-value">{AVAILABLE_MODELS.find(m => m.id === msg.model)?.name || msg.model}</span>
                  </div>
                  <div className="metrics-row">
                    <span className="metric-label">Latency:</span>
                    <span className="metric-value">{msg.metrics.latency.totalResponseTime}ms (TTFT: {msg.metrics.latency.timeToFirstToken}ms)</span>
                  </div>
                  <div className="metrics-row">
                    <span className="metric-label">Tokens:</span>
                    <span className="metric-value">In: {msg.metrics.tokens.input} | Out: {msg.metrics.tokens.output} | Total: {msg.metrics.tokens.total}</span>
                  </div>
                  <div className="metrics-row">
                    <span className="metric-label">Cost:</span>
                    <span className="metric-value">${msg.metrics.cost.total.toFixed(6)} (In: ${msg.metrics.cost.input.toFixed(6)} | Out: ${msg.metrics.cost.output.toFixed(6)})</span>
                  </div>
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="message assistant">
              <div className="message-content loading">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="model-selector-container">
          <label htmlFor="model-select">Model:</label>
          <select
            id="model-select"
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="model-select"
            disabled={loading}
          >
            {AVAILABLE_MODELS.map((model) => (
              <option key={model.id} value={model.id}>
                {model.name} - {model.description}
              </option>
            ))}
          </select>
        </div>

        <form onSubmit={sendMessage} className="input-form">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your message..."
            className="input-field"
            disabled={loading}
          />
          <button type="submit" disabled={loading || !input.trim()} className="send-button">
            Send
          </button>
          <button type="button" onClick={handleRickroll} className="lucky-button">
            I'm Feeling Lucky
          </button>
        </form>
      </div>

      {showRickroll && (
        <div className="rickroll-modal" onClick={closeRickroll}>
          <div className="rickroll-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={closeRickroll}>×</button>
            <h2>Never Gonna Give You Up!</h2>
            <img
              src="https://media.giphy.com/media/Vuw9m5wXviFIQ/giphy.gif"
              alt="Rickroll"
              className="rickroll-gif"
            />
            <audio autoPlay loop>
              <source src="/rickroll.MP3" type="audio/mpeg" />
            </audio>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <Authenticator hideSignUp={true}>
      {({ signOut, user }) => <Chat signOut={signOut} user={user} awsConfig={awsConfig} />}
    </Authenticator>
  );
}

export default App;
